import * as React from 'react';

import { LucideArrowLeft, LucideDot } from 'lucide-react';

import { Button, cn, Tab } from '../..';
import { AsyncBoundary } from '../ui/async';

type NotificationBanner = {
  label: string | React.ReactNode;
  type: 'success' | 'failure' | 'neutral';
} | null;

type EntityDetailsProps<
  Tabs extends Record<
    string,
    { title: string | React.ReactNode; content: React.ComponentType }
  >,
> = {
  onBackPressed?: () => void;
  primaryTitle: React.ReactNode;
  secondaryTitle?: React.ReactNode;
  subtitle?: React.ReactNode;
  badges?: React.ReactNode;
  headerActions?: React.ReactNode;
  tabs: Tabs;
  currentTab: keyof Tabs | undefined;
  onChangeTab: (tab: keyof Tabs) => void;
  onBeforeChangeTab?: (newTab: keyof Tabs) => boolean | void;
  className?: string;
  notificationBanner?: NotificationBanner;
  headerRef?: React.Ref<HTMLDivElement>;
  error?: Error | null;
  loading?: boolean;
  asyncBoundaryShowLoadingLabel?: boolean;
};

export const EntityDetails = <
  Tabs extends Record<
    string,
    { title: string | React.ReactNode; content: React.ComponentType }
  >,
>(
  props: EntityDetailsProps<Tabs>,
) => {
  const currentTab = props.currentTab ?? Object.keys(props.tabs)[0];
  const Content = props.tabs[currentTab]?.content ?? React.Fragment;
  const showAsyncBoundary =
    props.loading === true ||
    (props.error != null && props.error !== undefined);

  const showTabs = Object.keys(props.tabs).length > 1;

  const content = (
    <div
      className={cn('@EntityDetails flex size-full flex-col', props.className)}
    >
      <div
        className={cn(
          props.notificationBanner?.type === 'success' && 'bg-success',
          props.notificationBanner?.type === 'failure' && 'bg-failure',
          props.notificationBanner?.type === 'neutral' && 'bg-neutral/10',
        )}
      >
        {props.notificationBanner && (
          <div
            className={cn(
              'flex min-h-10 items-center justify-center text-sm text-white',
              props.notificationBanner?.type === 'neutral' && 'text-neutral/60',
            )}
          >
            {props.notificationBanner.label}
          </div>
        )}
        <div
          ref={props.headerRef}
          className={cn(
            '@Header flex flex-col gap-1 border-b border-neutral/10 bg-white px-5 pt-4',
            !showTabs && 'pb-4.5',
            props.notificationBanner && 'rounded-t-[9px]',
          )}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {props.onBackPressed && (
                <Button
                  variant={'outline'}
                  color={'neutral'}
                  className="border-neutral/20 px-3 py-0"
                  onClick={props.onBackPressed}
                >
                  <LucideArrowLeft size={16} />
                </Button>
              )}
              <div>
                <div className="flex items-center gap-1 text-xl">
                  <div className="font-semibold">{props.primaryTitle}</div>
                  {props.secondaryTitle && (
                    <>
                      <LucideDot />
                      <div className="font-light">{props.secondaryTitle}</div>
                    </>
                  )}
                  {props.badges && (
                    <div className="ml-2 flex items-center gap-1">
                      {props.badges}
                    </div>
                  )}
                </div>
                {props.subtitle && (
                  <div className="flex items-center gap-1 text-sm text-neutral/50">
                    {props.subtitle}
                  </div>
                )}
              </div>
            </div>
            <div>{props.headerActions}</div>
          </div>
          {showTabs && (
            <div className="flex gap-5">
              {(Object.keys(props.tabs) as Array<keyof Tabs>).map((key) => (
                <Tab
                  key={key as string}
                  selected={currentTab === key}
                  onClick={() => {
                    if (props.onBeforeChangeTab) {
                      const shouldChange = props.onBeforeChangeTab(key);
                      if (shouldChange === false) return;
                    }
                    props.onChangeTab(key);
                  }}
                >
                  {props.tabs[key].title}
                </Tab>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="@Content no-scrollbar flex h-full flex-col overflow-y-auto">
        <Content />
      </div>
    </div>
  );

  if (showAsyncBoundary) {
    return (
      <AsyncBoundary
        error={props.error ?? null}
        loading={props.loading}
        showLoadingLabel={props.asyncBoundaryShowLoadingLabel ?? true}
      >
        {content}
      </AsyncBoundary>
    );
  }

  return content;
};
